import React from 'react'
import { Box, Grid, Typography } from '@mui/material'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import AppsRoundedIcon from '@mui/icons-material/AppsRounded'
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded'
import { ActivityTable } from '../../components/dashboard/ActivityTable'
import { AppShortcutCard } from '../../components/dashboard/AppShortcutCard'
import { MetricCard } from '../../components/dashboard/MetricCard'
import { useAuth } from '../../context/AuthContext'
import { getAssignedApplicationLinks, getUserPanelApplicationIcon, getUserRecentActivity } from '../../services/apis'
import { getApiErrorMessage } from '../../utils/apiData'
import { getUsuarioFromSession } from '../../utils/permisos'

const mapApplicationToCard = (application) => ({
  ...application,
  title: application.title || application.name || application.code,
  description: application.description || `Acceso habilitado a ${application.title || application.code}.`,
  icon: getUserPanelApplicationIcon(application),
})

const buildMetrics = ({ appCount, recentCount }) => [
  { title: 'Aplicaciones disponibles', value: String(appCount), action: 'Ver todas', path: '/aplicaciones', icon: <AppsRoundedIcon /> },
  { title: 'Accesos recientes', value: String(recentCount), action: 'Ver historial', path: '/accesos-recientes', icon: <AccessTimeRoundedIcon /> },
  { title: 'Tareas pendientes', value: '0', action: 'Ver pendientes', path: '/tareas', icon: <PendingActionsRoundedIcon /> },
]

export const Home = () => {
  const { session } = useAuth()
  const user = getUsuarioFromSession(session)
  const applications = React.useMemo(() => (
    getAssignedApplicationLinks(session).map(mapApplicationToCard)
  ), [session])
  const [activityRows, setActivityRows] = React.useState([])
  const [activityError, setActivityError] = React.useState('')

  React.useEffect(() => {
    let active = true

    const loadActivity = async () => {
      setActivityError('')

      try {
        const rows = await getUserRecentActivity({ session, userId: user?.id, limit: 5 })
        if (active) setActivityRows(rows)
      } catch (error) {
        if (active) {
          setActivityRows([])
          setActivityError(getApiErrorMessage(error, 'No se ha podido cargar la actividad reciente.'))
        }
      }
    }

    loadActivity()

    return () => {
      active = false
    }
  }, [session, user?.id])

  const metrics = buildMetrics({
    appCount: applications.length,
    recentCount: activityRows.length,
  })

  return (
    <Box className="page-fade">
      <Box className="page-heading">
        <Typography variant="h4">Bienvenido</Typography>
        <Typography>Desde aquí puedes acceder rápidamente a tus aplicaciones y consultar tu actividad.</Typography>
      </Box>

      <Grid container spacing={2.4}>
        {metrics.map((metric) => (
          <Grid key={metric.title} size={{ xs: 12, sm: 6, lg: 3 }}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2.4} sx={{ mt: 1.4 }}>
        <Grid size={{ xs: 12 }}>
          <Box className="dashboard-section-card">
            <Typography className="section-title">Acceso rápido a aplicaciones</Typography>
            <Grid container spacing={1.3}>
              {applications.map((app) => (
                <Grid key={app.id || app.code || app.title} size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
                  <AppShortcutCard {...app} />
                </Grid>
              ))}
              {applications.length === 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography className="admin-user-empty">Todavía no tienes aplicaciones asignadas.</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ActivityTable
            title="Últimas aplicaciones usadas"
            rows={activityRows}
            footer="Ver historial completo"
            footerPath="/accesos-recientes"
            emptyMessage={activityError || 'No hay actividad reciente.'}
          />
        </Grid>
      </Grid>
    </Box>
  )
}
